import { Component, Output, Input, EventEmitter, signal, OnInit, OnChanges, AfterViewInit, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from "@angular/forms";
import { Charge, ChargeItem, ChargeItemDocumentType } from "../models/charge.models";
import { CurrencyClpPipe, parseCurrencyClp, formatCurrencyInput } from "../pipes/currency-clp.pipe";
import { ModalComponent } from "../ui/modal/modal.component";
import { ButtonComponent } from "../ui/button/button.component";
import { ConfirmChargeDialogComponent } from "../confirm-charge-dialog/confirm-charge-dialog.component";
import { UnsavedChangesDialogComponent } from "../unsaved-changes-dialog/unsaved-changes-dialog.component";
import { LineItem } from "../../../models/online-checkout/online-checkout.interface";
import { v4 as uuidv4 } from "uuid";
import { OnlineCheckoutService } from "../../../services/online-checkout.service";
import { TooltipModule } from "primeng/tooltip";
import { Select } from 'primeng/select';

@Component({
  selector: "app-new-charge-dialog",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CurrencyClpPipe, ModalComponent, ConfirmChargeDialogComponent, UnsavedChangesDialogComponent, TooltipModule, Select],

  templateUrl: "./new-charge-dialog.component.html",
  styleUrls: ["./new-charge-dialog.component.scss"],
})
export class NewChargeDialogComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() chargeData: Charge | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() closeBlank = new EventEmitter<void>();
  @Output() chargeCreated = new EventEmitter<void>();

  form: FormGroup;
  showConfirmDialog = signal(false);
  showUnsavedChangesDialog = signal(false);
  // chargeItemDocumentType = ChargeItemDocumentType;
  private originalItems: ChargeItem[] = [];

  docTypeOptions = [
    { label: 'N° Factura', value: ChargeItemDocumentType.Factura },
    { label: 'Orden de Cobro', value: ChargeItemDocumentType.OC },
    { label: 'N° Referencia', value: ChargeItemDocumentType.Referencia },
  ];
  private hasUnsavedChanges = false;

  constructor(private fb: FormBuilder, private onlineCheckoutService: OnlineCheckoutService) {
    this.form = this.fb.group({
      lines: this.fb.array([this.createLineGroup()]),
    });
    
    // Detectar cambios en el formulario
    this.form.valueChanges.subscribe(() => {
      this.hasUnsavedChanges = this.checkForUnsavedChanges();
    });
  }

  // Custom validator for amount field
  private amountValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { required: true };
    }
    
    const numericValue = parseCurrencyClp(value);
    
    if (numericValue === 0) {
      return { invalidAmount: true };
    }
    
    if (numericValue > 999999999) {
      return { maxDigits: true };
    }
    
    return null;
  }

  ngOnInit(): void {
    console.log("NewChargeDialog ngOnInit - chargeData:", this.chargeData);
    this.checkAndLoadChargeData();
  }

  ngAfterViewInit(): void {
    console.log("NewChargeDialog ngAfterViewInit - chargeData:", this.chargeData);
    // Use setTimeout to ensure chargeData is available after Angular's change detection cycle
    setTimeout(() => {
      console.log("NewChargeDialog setTimeout - chargeData:", this.chargeData);
      this.checkAndLoadChargeData();
    }, 0);
  }

  private checkAndLoadChargeData(): void {
    if (this.chargeData?.items?.length) {
      console.log("Loading charge items:", this.chargeData.items);
      this.loadChargeItems();
    } else {
      console.log("No charge data or items to load");
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["chargeData"]) {
      console.log("chargeData changed:", changes["chargeData"]);
      if (this.chargeData) {
        if (this.chargeData?.items?.length) {
          this.loadChargeItems();
        } else {
          console.log("No charge items to load - items length:", this.chargeData?.items?.length);
        }
      } else {
        console.log("chargeData is null/undefined");
      }
    }
  }

  private loadChargeItems(): void {
    console.log("loadChargeItems called");
    if (!this.chargeData?.items) {
      return;
    }

    // Store original items for comparison
    this.originalItems = JSON.parse(JSON.stringify(this.chargeData.items));

    // Clear existing lines
    while (this.lines.length > 0) {
      this.lines.removeAt(0);
    }

    // Add lines from charge data
    this.chargeData.items.forEach((item: ChargeItem, index: number) => {
      console.log(`Item ${index}:`, item);
      const lineGroup = this.fb.group({
        docType: [item.documentType || "", Validators.required],
        reference: [item.documentNumber || "", Validators.required],
        description: [item.description || "", Validators.required],
        amountCLP: [formatCurrencyInput(item.amount?.toString() || "0"), [Validators.required, this.amountValidator.bind(this)]],
      });

      // Force update to trigger change detection
      lineGroup.get("docType")?.updateValueAndValidity();
      this.lines.push(lineGroup);
    });
  }

  get lines(): FormArray {
    return this.form.get("lines") as FormArray;
  }

  get totalAmount(): number {
    return this.lines.controls.reduce((total, control) => {
      const amount = parseCurrencyClp(control.get("amountCLP")?.value || "0");
      return total + amount;
    }, 0);
  }

  get paymentLinkUrl(): string | null {
    return this.chargeData?.payment_link_url || null;
  }

  get isFormValid(): boolean {
    return this.form.valid && this.totalAmount > 0 && this.totalAmount <= 999999999;
  }

  get isTotalAmountExceeded(): boolean {
    return this.totalAmount > 999999999;
  }

  createLineGroup(): FormGroup {
    return this.fb.group({
      docType: ["", Validators.required],
      reference: ["", Validators.required],
      description: ["", Validators.required],
      amountCLP: ["", [Validators.required, this.amountValidator.bind(this)]],
    });
  }

  addLine(): void {
    this.lines.push(this.createLineGroup());
    queueMicrotask(() => this.focusNewLine());
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) {
      this.lines.removeAt(index);
      this.form.updateValueAndValidity();
      this.recalcTotal();
    }
  }

  onAmountInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const formatted = formatCurrencyInput(target.value);
    const control = this.lines.at(index).get("amountCLP");
    control?.setValue(`${formatted}`, { emitEvent: true });
  }

  onAmountKeypress(event: KeyboardEvent, index: number): void {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const key = event.key;
    
    // Solo permitir números
    if (!/[0-9]/.test(key)) {
      return;
    }
    
    // Simular el valor después de agregar el caracter
    const newValue = currentValue + key;
    const numericValue = parseCurrencyClp(newValue);
    
    // Bloquear si excede 9 dígitos
    if (numericValue > 999999999) {
      event.preventDefault();
    }
  }

  onAmountPaste(event: ClipboardEvent, index: number): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const numericValue = parseCurrencyClp(pastedText);
    
    // Bloquear paste si excede 9 dígitos
    if (numericValue > 999999999) {
      event.preventDefault();
    }
  }

  onAmountBlur(index: number): void {
    const control = this.lines.at(index).get("amountCLP");
    control?.markAsTouched();
  }

  getAmountError(index: number): string | null {
    const control = this.lines.at(index).get('amountCLP');
    if (control?.errors) {
      if (control.errors['required'] && control.touched) {
        return 'El monto es requerido';
      }
      if (control.errors['invalidAmount'] && control.touched) {
        return 'El monto debe ser mayor a 0';
      }
      if (control.errors['maxDigits']) {
        return 'El monto del cobro debe tener máximo 9 dígitos';
      }
    }
    return null;
  }

  onSelectChange(event: any, index: number): void {
    // const target = event.target as HTMLSelectElement;
    // const control = this.lines.at(index).get("docType");
    // control?.setValue(target.value);
  }

  onConfirm(): void {
    if (!this.isFormValid) {
      return;
    }

    // Caso 1: Nuevo cobro (no existe chargeData)
    if (!this.chargeData) {
      this.showConfirmDialog.set(true);
      return;
    }

    // Caso 2: Cobro existente - verificar cambios
    const currentItems = this.getFormData();
    if (this.hasChanges(currentItems)) {
      this.updateExistingChargeAndConfirm(currentItems);
    } else {
      this.showConfirmDialog.set(true);
    }
  }
  

  onSaveAndClose(): void {
    if (!this.isFormValid) {
      this.close.emit();
      return;
    }

    // Caso 1: Nuevo cobro (no existe chargeData)
    if (!this.chargeData) {
      this.createNewCharge();
      return;
    }

    // Caso 2: Cobro existente - verificar cambios
    const currentItems = this.getFormData();
    if (this.hasChanges(currentItems)) {
      this.updateExistingCharge(currentItems);
    } else {
      // No hay cambios, simplemente cerrar
      console.log("ℹ️ No hay cambios, simplemente cerrar");
      this.closeBlank.emit();
    }
  }

  private createNewCharge(): void {
    const orderNumber = new Date().getTime() + "";
    const merchantId = localStorage.getItem("auth.merchant_id") || "";
    const authToken = localStorage.getItem("auth.access_token") || "";
    const idempotencyKey = crypto.randomUUID();

    const formData = this.getFormData();
    const lineItems: LineItem[] = formData.map((item) => ({
      amount: item.amount,
      quantity: 1,
      product: {
        sku: `${item.documentNumber}`,
        name: `${item.documentType}|${item.documentNumber}`,
        description: item.description,
        unit_amount: item.amount,
        picture_url: "https://via.placeholder.com/150",
        category_id: "General",
      },
    }));

    this.onlineCheckoutService.createOnlineCheckout(orderNumber, this.totalAmount, merchantId, lineItems, authToken, idempotencyKey).subscribe({
      next: (response) => {
        console.log("response onContinue=> ", response);
        this.close.emit();
      },
      error: (error) => {
        console.error("Error creating online checkout:", error);
        this.close.emit();
      },
    });
  }

  private updateExistingCharge(currentItems: ChargeItem[]): void {
    const authToken = localStorage.getItem("auth.access_token") || "";
    const chargeId = this.chargeData?.id || "";

    const lineItems: LineItem[] = currentItems.map((item) => ({
      amount: item.amount,
      quantity: 1,
      product: {
        sku: `${item.documentNumber}`,
        name: `${item.documentType}|${item.documentNumber}`,
        description: item.description,
        unit_amount: item.amount,
        picture_url: "https://via.placeholder.com/150",
        category_id: "General",
      },
    }));

    this.onlineCheckoutService.updateOnlineCheckout(chargeId, this.totalAmount, lineItems, authToken).subscribe({
      next: (response) => {
        console.log("Charge updated successfully:", response);
        this.close.emit();
      },
      error: (error) => {
        console.error("Error updating charge:", error);
        this.close.emit();
      },
    });
  }

  private updateExistingChargeAndConfirm(currentItems: ChargeItem[]): void {
    const authToken = localStorage.getItem("auth.access_token") || "";
    const chargeId = this.chargeData?.id || "";

    const lineItems: LineItem[] = currentItems.map((item) => ({
      amount: item.amount,
      quantity: 1,
      product: {
        sku: `${item.documentNumber}`,
        name: `${item.documentType}|${item.documentNumber}`,
        description: item.description,
        unit_amount: item.amount,
        picture_url: "https://via.placeholder.com/150",
        category_id: "General",
      },
    }));

    this.onlineCheckoutService.updateOnlineCheckout(chargeId, this.totalAmount, lineItems, authToken).subscribe({
      next: (response) => {
        console.log("Charge updated successfully:", response);
        this.showConfirmDialog.set(true);
      },
      error: (error) => {
        console.error("Error updating charge:", error);
      },
    });
  }

  private hasChanges(currentItems: ChargeItem[]): boolean {
    if (currentItems.length !== this.originalItems.length) {
      return true;
    }

    return currentItems.some((current, index) => {
      const original = this.originalItems[index];
      return current.documentType !== original.documentType || current.documentNumber !== original.documentNumber || current.description !== original.description || current.amount !== original.amount;
    });
  }

  closeConfirmDialog(): void {
    this.showConfirmDialog.set(false);
  }

  onChargeConfirmed(): void {
    this.showConfirmDialog.set(false);
    this.chargeCreated.emit();
  }

  getFormData(): ChargeItem[] {
    return this.lines.value.map((line: any) => ({
      documentType: line.docType,
      documentNumber: line.reference,
      description: line.description,
      amount: parseCurrencyClp(line.amountCLP),
    }));
  }

  private focusNewLine(): void {
    const newIndex = this.lines.length - 1;
    const newRow = document.querySelector(`[data-row="${newIndex}"] input`);
    if (newRow) {
      (newRow as HTMLElement).focus();
    }
  }

  private recalcTotal(): void {
    // Trigger change detection for total calculation
  }

  onCloseClick(): void {
    if (this.hasUnsavedChanges && this.isFormValid) {
      this.showUnsavedChangesDialog.set(true);
    } else {
      this.closeBlank.emit();
    }
  }

  onUnsavedChangesClose(): void {
    this.showUnsavedChangesDialog.set(false);
    this.closeBlank.emit();
  }

  onUnsavedChangesSaveAndClose(): void {
    this.showUnsavedChangesDialog.set(false);
    this.onSaveAndClose();
  }

  private checkForUnsavedChanges(): boolean {
    const currentItems = this.getFormData();
    
    // Si es un nuevo cobro y hay datos en el formulario
    if (!this.chargeData) {
      return currentItems.some(item => 
        item.documentType || item.documentNumber || item.description || item.amount > 0
      );
    }
    
    // Si es un cobro existente, verificar cambios
    return this.hasChanges(currentItems);
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
